/**
 * PayPal Smart Payment Button — renders inline and opens a POPUP for the
 * customer to approve payment, unlike a full-page redirect (which loses all
 * booking state, requiring the same "?screen=..." URL tricks we needed for
 * Stripe elsewhere). The booking page never navigates away.
 *
 * Web only — PayPal's JS SDK is a browser script; native mobile falls back
 * to the existing off-platform PayPal flow.
 */
import React, { useEffect, useRef, useState } from 'react';
import { View, Text, StyleSheet, ActivityIndicator, Platform } from 'react-native';

interface PayPalSmartButtonProps {
  clientId: string;
  amountUsd: number;
  createOrder: () => Promise<string>;
  onApprove: (orderId: string) => Promise<{ success: boolean; error?: string }>;
  onError?: (message: string) => void;
  disabled?: boolean;
}

const sdkLoadPromiseByClientId: Record<string, Promise<void>> = {};

function loadPaypalSdk(clientId: string): Promise<void> {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.reject(new Error('PayPal is only available in a browser.'));
  }
  if ((window as any).paypal && (window as any).__bookaptPaypalClientId === clientId) {
    return Promise.resolve();
  }
  if (sdkLoadPromiseByClientId[clientId]) {
    return sdkLoadPromiseByClientId[clientId];
  }

  const promise = new Promise<void>((resolve, reject) => {
    // Only one merchant's SDK can be loaded at a time on a given page — drop
    // a previously-loaded one for a different client id before loading this one.
    const existing = document.getElementById('bookapt-paypal-sdk');
    if (existing) existing.remove();
    delete (window as any).paypal;

    const script = document.createElement('script');
    script.id = 'bookapt-paypal-sdk';
    script.src = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(
      clientId
    )}&currency=USD&intent=capture`;
    script.onload = () => {
      (window as any).__bookaptPaypalClientId = clientId;
      resolve();
    };
    script.onerror = () => reject(new Error('Could not load PayPal.'));
    document.body.appendChild(script);
  });
  sdkLoadPromiseByClientId[clientId] = promise;
  return promise;
}

const PayPalSmartButton: React.FC<PayPalSmartButtonProps> = ({
  clientId,
  amountUsd,
  createOrder,
  onApprove,
  onError,
  disabled,
}) => {
  const containerRef = useRef<any>(null);
  const [loading, setLoading] = useState(true);
  const [renderError, setRenderError] = useState<string | null>(null);

  useEffect(() => {
    if (Platform.OS !== 'web' || !clientId) return;
    let cancelled = false;

    loadPaypalSdk(clientId)
      .then(() => {
        if (cancelled || !containerRef.current) return;
        const paypal = (window as any).paypal;
        if (!paypal?.Buttons) {
          setRenderError('PayPal is unavailable right now.');
          setLoading(false);
          return;
        }
        containerRef.current.innerHTML = '';
        paypal
          .Buttons({
            style: { layout: 'vertical', color: 'gold', shape: 'rect', label: 'paypal' },
            createOrder: async () => {
              try {
                return await createOrder();
              } catch (e: any) {
                onError?.(e?.message || 'Could not start PayPal checkout.');
                throw e;
              }
            },
            onApprove: async (data: { orderID: string }) => {
              const result = await onApprove(data.orderID);
              if (!result.success) {
                onError?.(result.error || 'Payment was not completed.');
              }
            },
            onCancel: () => {
              /* customer closed the popup — nothing to do, no booking was created */
            },
            onError: (err: any) => {
              console.error('PayPal Smart Button error:', err);
              onError?.('PayPal encountered an error. Please try again.');
            },
          })
          .render(containerRef.current);
        setLoading(false);
      })
      .catch((e) => {
        if (!cancelled) {
          setRenderError(e?.message || 'Could not load PayPal.');
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  if (Platform.OS !== 'web') {
    return null;
  }

  return (
    <View style={styles.wrap}>
      <Text style={styles.totalText}>Total: ${amountUsd.toFixed(2)}</Text>
      {loading && (
        <View style={styles.loadingRow}>
          <ActivityIndicator size="small" color="#0070ba" />
          <Text style={styles.loadingText}>Loading PayPal…</Text>
        </View>
      )}
      {renderError ? <Text style={styles.errorText}>{renderError}</Text> : null}
      {/* @ts-ignore raw DOM element — web only, PayPal SDK renders directly into it */}
      <div
        ref={containerRef}
        style={{
          opacity: disabled ? 0.5 : 1,
          pointerEvents: disabled ? 'none' : 'auto',
          display: loading ? 'none' : 'block',
        }}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  wrap: {
    marginTop: 4,
  },
  totalText: {
    fontSize: 15,
    fontWeight: '700',
    color: '#1C1C1E',
    marginBottom: 8,
  },
  loadingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 12,
  },
  loadingText: {
    fontSize: 14,
    color: '#666',
  },
  errorText: {
    fontSize: 13,
    color: '#c00',
    marginBottom: 8,
  },
});

export default PayPalSmartButton;
